/*
  Ticker: the claim list scrolls continuously — on every viewport size
  now, not just mobile (desktop opts into the same kinetic marquee
  look on purpose, not as an overflow fallback). Achieved by repeating
  the item set into two equal halves (so the CSS animation in
  highrise-home.css can loop translateX(0) to translateX(-50%)
  seamlessly) and letting CSS handle the motion.

  Each half repeats the original items enough times to comfortably
  exceed the current viewport width — a single, undoubled copy is
  usually much narrower than a wide desktop viewport, and animating
  that as one "half" would show a visible gap of blank space partway
  through the loop before it repeats.

  Rebuilt (not just measured once) on both `document.fonts.ready` and
  window resize: the very first measurement runs at DOMContentLoaded,
  before the custom web font has necessarily swapped in (font-display:
  swap starts with the fallback system font) and before an in-progress
  browser resize/orientation change has settled — either one leaves the
  original build's repeat count sized for a width that's since changed,
  which is exactly what surfaced as a stray gap of whitespace partway
  through the loop and the last item before the seam reading as cut
  off mid-word instead of wrapping cleanly. Rebuilding from the
  original (unrepeated) item set each time, rather than re-measuring
  the already-doubled/repeated list, avoids compounding repeats across
  rebuilds.

  Speed reads as constant regardless of how wide a given row ends up
  (more items, wider viewport, more repeats) because duration is set
  from measured content width divided by a fixed px/s speed, rather
  than a flat animation-duration — a fixed duration would make wider
  rows visibly race by faster than narrow ones (translateX(-50%) covers
  more absolute distance in the same time).
*/
(function () {
  var PX_PER_SECOND = 100; // audit 2026-09-01: team asked for a faster ticker

  // Per-section override via data-ticker-speed, so the theme editor can
  // dial speed without a code change.
  function speedFor(list) {
    var v = parseFloat(list.getAttribute('data-ticker-speed'));
    return v > 0 ? v : PX_PER_SECOND;
  }
  var WIDTH_MARGIN = 1.6; // extra headroom over one viewport width, so drift from a late font swap or an in-flight resize can't outrun the built content

  function initTicker(list) {
    // Snapshot the pristine, unrepeated markup once — build() always
    // rebuilds from this rather than the already-repeated list, so
    // re-running it on font-load/resize can't compound repeats on top
    // of repeats.
    var originalHtml = list.innerHTML;
    function speed() { return speedFor(list); }
    var started = false;

    function build() {
      var wasVisible = started;
      list.innerHTML = originalHtml;
      var items = Array.from(list.children);
      if (items.length === 0) return;

      var baseWidth = list.scrollWidth;
      if (baseWidth === 0) return;

      var repeats = Math.max(1, Math.ceil((window.innerWidth * WIDTH_MARGIN) / baseWidth));

      var fragment = document.createDocumentFragment();
      for (var r = 0; r < repeats - 1; r++) {
        items.forEach(function (item) {
          var clone = item.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          fragment.appendChild(clone);
        });
      }
      list.appendChild(fragment);

      var halfWidth = list.scrollWidth;

      var secondHalf = document.createDocumentFragment();
      Array.from(list.children).forEach(function (item) {
        var clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        secondHalf.appendChild(clone);
      });
      list.appendChild(secondHalf);

      // The loop distance must be the measured pixel offset of the second
      // half's first item, NOT translateX(-50%): a percentage transform
      // resolves against this element's own border box (width:100% of the
      // section), while the content overflows far past it. Half the border
      // box is nowhere near the seam, so the row slid a short way and
      // snapped back instead of looping. Measure the seam and shift by px.
      var children = list.children;
      var seamIndex = Math.floor(children.length / 2);
      var shift = children[seamIndex].offsetLeft - children[0].offsetLeft;
      if (shift <= 0) shift = halfWidth; // defensive: never animate to 0

      list.style.setProperty('--hn-ticker-shift', shift + 'px');
      list.style.setProperty('--hn-ticker-duration', (shift / speed()) + 's');

      if (wasVisible) list.classList.add('highrise-trust-bar__list--ticker');
    }

    build();

    function startTicker() {
      started = true;
      list.classList.add('highrise-trust-bar__list--ticker');
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            startTicker();
            observer.unobserve(list);
          }
        });
      });
      observer.observe(list);
    } else {
      startTicker();
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(build);
    }

    var resizeRaf = null;
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastWidth) return; // mobile browsers fire resize on address-bar show/hide alone; only rebuild on an actual width change
      lastWidth = window.innerWidth;
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(build);
    });
  }

  function init() {
    document.querySelectorAll('[data-trust-bar-ticker]').forEach(initTicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
